import { useMemo, useRef, useState, useEffect } from "react";

export default function Review() {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const autoPlayRef = useRef(null);
  const isHoveringRef = useRef(false);

  // Images and reviews
  const reviews = useMemo(
    () => [
      {
        img: require("../assets/cover/review/1.png"),
        text: "Excelente calidad y sin interrupciones. Estoy muy contento con los canales en español.",
        name: "Pierre",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/2.png"),
        text: "Audio y video perfectos. La app es rápida y simple.",
        name: "Marie",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/3.png"),
        text: "Puedo ver todos los partidos sin problema. Recomendado.",
        name: "Antoine",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/4.png"),
        text: "Configuración fácil y soporte atento. Excelente experiencia.",
        name: "Anne",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/5.png"),
        text: "Las películas y series se cargan rápidamente. Interfaz moderna.",
        name: "Jean",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/6.png"),
        text: "Ideal para ver la Ligue 1 y la Champions sin cortes.",
        name: "Camille",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/7.png"),
        text: "Funciona perfectamente en mi TV y móvil. Muy satisfecho.",
        name: "François",
        country: "ES",
      },
      {
        img: require("../assets/cover/review/8.png"),
        text: "Relación calidad-precio increíble. Lo uso a diario.",
        name: "Isabelle",
        country: "ES",
      },
    ],
    []
  );

  // Update active index on scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cardWidth = el.firstElementChild?.clientWidth || 1;
      const scrollPosition = el.scrollLeft;
      const cardWithGap = cardWidth + 24; // gap-6 ≈ 24px
      const index = Math.round(scrollPosition / cardWithGap);
      setActiveIndex(index % reviews.length);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [reviews.length]);

  // Auto-play infinite carousel
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const startAutoPlay = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      
      autoPlayRef.current = setInterval(() => {
        if (isHoveringRef.current) return;
        
        const cardWidth = el.firstElementChild?.clientWidth || el.clientWidth / 3;
        const cardWithGap = cardWidth + 24;
        const currentScroll = el.scrollLeft;
        
        // Calculer la position du premier set de reviews
        const oneSetWidth = reviews.length * cardWithGap;
        
        // Si on dépasse le deuxième set, revenir au premier set de manière invisible
        if (currentScroll >= oneSetWidth * 2 - 10) {
          el.scrollTo({ left: currentScroll - oneSetWidth, behavior: "auto" });
        }
        
        // Scroller vers la prochaine carte
        el.scrollBy({ left: cardWithGap, behavior: "smooth" });
      }, 3000); // Défile toutes les 3 secondes
    };

    startAutoPlay();

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [reviews.length]);

  // Arrow scroll helpers (optional for desktop)
  const scrollByCards = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.clientWidth || el.clientWidth / 3;
    el.scrollBy({ left: direction * (cardWidth + 24), behavior: "smooth" });
  };

  return (
    <>
      <section className="py-12 sm:py-16 bg-slate-50 relative">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto relative">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-auto font-black mb-2 sm:mb-3 max-w-4xl leading-snug text-center">
              Lo Que Dicen{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                Nuestros Usuarios
              </span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Comentarios reales de clientes reales
            </p>
          </div>

          {/* Carousel - manual swipe/drag only */}
          <div className="relative">
            {/* left/right buttons for desktop */}
            <button
              onClick={() => scrollByCards(-1)}
              className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow hover:shadow-md border border-slate-200"
              aria-label="Prev"
            >
              ‹
            </button>
            <button
              onClick={() => scrollByCards(1)}
              className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow hover:shadow-md border border-slate-200"
              aria-label="Next"
            >
              ›
            </button>

            <div
              ref={containerRef}
              className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              onMouseEnter={() => (isHoveringRef.current = true)}
              onMouseLeave={() => (isHoveringRef.current = false)}
              onTouchStart={() => (isHoveringRef.current = true)}
              onTouchEnd={() => {
                setTimeout(() => (isHoveringRef.current = false), 1000);
              }}
            >
              {[...reviews, ...reviews, ...reviews].map((r, idx) => (
                <div
                  key={idx}
                  className="flex-none w-[85%] sm:w-[70%] md:w-1/2 lg:w-1/3 snap-start bg-white rounded-xl sm:rounded-2xl shadow-md border border-slate-100 p-4 sm:p-5"
                >
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="overflow-hidden rounded-lg sm:rounded-xl bg-slate-100 h-36 sm:h-44 md:h-56">
                      <img
                        src={r.img}
                        alt={`review-${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-[#090979] mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span key={i} className="text-sm sm:text-base">
                            ★
                          </span>
                        ))}
                      </div>
                      <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                        {r.text}
                      </p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                        <span className="inline-flex w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]"></span>
                        <div>
                          <p className="text-slate-900 font-semibold text-xs sm:text-sm">
                            {r.name}
                          </p>
                          <p className="text-slate-500 text-xs">{r.country}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {reviews.map((_, i) => (
                <span
                  key={i}
                  className={`w-2 h-2 rounded-full transition ${
                    i === activeIndex ? "bg-slate-800" : "bg-slate-400/50"
                  }`}
                ></span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
